import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteComment } from '@/api/mutations/useCommentMutations';
import { useCommentableTextAtoms } from '@/comments';
import { MoreVerticalButton } from '@/components/MoreVerticalButton/MoreVerticalButton';
import { useTextAtomSave } from '@/data/editorDataLayer';
import { useRequiredAppSelector } from '@/hooks';
import { modals } from '@/modals';
import { usePrefValue } from '@/preferences';
import type { IComment } from '@/types';
import {
    deleteCommentInTextAtomContent,
    invalidateAfterCommentUpdate,
    removeMarksFromString,
    textableTypeToTextableModel,
} from './helpers';

interface CommentItemHeaderProps {
    id: IComment['id'];
    textAtomId: IComment['commentable']['id'];
    handleEdit: () => void;
    textContent: IComment['commentable']['content'];
}

export const CommentItemHeaderActionsMore = (props: CommentItemHeaderProps) => {
    const { id, textAtomId, handleEdit, textContent } = props;
    const project_id = useRequiredAppSelector('projectId');
    const { t } = useTranslation();
    const episode_id = usePrefValue('editor_episode_id');
    const { textAtoms: commentableTextAtoms } = useCommentableTextAtoms();
    const textAtom = commentableTextAtoms?.[textAtomId];

    const { mutateAsync: deleteComment } = useDeleteComment();

    const saveSynopsisAtom = useTextAtomSave(
        {
            textableId: textAtom?.textable_id ?? 0,
            textableModel: textableTypeToTextableModel(textAtom?.textable_type),
            textAtomTypeId: textAtom?.text_atom_type_id ?? 1,
            successCallback: () => invalidateAfterCommentUpdate(episode_id)(),
        },
        textAtomId ? textAtom : undefined,
    );

    const handleDelete = useCallback(() => {
        modals.confirm({
            message: t('comment.branch'),
            cancelLabel: t('buttons.no-delete'),
            onConfirm: async () => {
                const newString = removeMarksFromString(textContent, [id.toString()]);
                await deleteComment({
                    project_id,
                    comment_id: id,
                    update_text_atom: newString,
                });
                saveSynopsisAtom(deleteCommentInTextAtomContent((textAtom?.content ?? '') as string, id));
            },
        });
    }, [deleteComment, id, project_id, saveSynopsisAtom, t, textAtom?.content, textContent]);

    const options = useMemo(
        () => [
            { title: 'buttons.delete', id, name: 'delete', function: handleDelete },
            { title: 'buttons.edit', id, name: 'edit', function: handleEdit },
        ],
        [id, handleDelete, handleEdit],
    );

    return <MoreVerticalButton options={options} padding="0" />;
};
